import { ServiceItem, SubServiceItem } from '../types';

export const servicesData: ServiceItem[] = [
  {
    id: 'ai-website',
    slug: 'ai-website-development',
    title: 'AI Website Development',
    shortDescription: 'Build intelligent, modern websites designed to create better digital experiences and support business growth.',
    fullDescription: 'We build next-generation web applications powered by generative AI, dynamic personalization, conversational search, and intelligent workflow automation.',
    iconName: 'Code2',
    accentColor: 'purple',
    challenges: [
      'Static websites with low user engagement and poor conversion rates',
      'Inability to provide personalized real-time user experiences',
      'Slow manual content generation and maintenance overhead',
      'Clunky search and discovery experiences for complex product catalogs'
    ],
    solutions: [
      'Intelligent dynamic content tailored to visitor intent and segment',
      'AI chatbot and assistant integration for 24/7 lead qualification',
      'Semantic search and vector-based knowledge discovery engines',
      'High-performance headless architecture for ultra-fast loading speeds'
    ],
    benefits: [
      'Up to 3x increase in user session engagement and conversion',
      'Automated customer query handling reducing support response times',
      'Enterprise-grade SEO architecture and Core Web Vitals optimization',
      'Scalable cloud infrastructure supporting millions of global interactions'
    ],
    process: [
      { step: '01', title: 'AI Discovery & Architecture', description: 'Analyze user journeys, conversion barriers, and AI opportunity areas.' },
      { step: '02', title: 'UX & Intelligent Interface Design', description: 'Craft clean, intuitive component systems and conversational touchpoints.' },
      { step: '03', title: 'Full-Stack Development & AI Training', description: 'Implement React/Next.js frontends integrated with vector embeddings and LLM pipelines.' },
      { step: '04', title: 'Performance Testing & Deployment', description: 'Rigorous latency testing, security audits, and edge CDN deployment.' }
    ],
    faqs: [
      {
        question: 'How does AI website development differ from standard web development?',
        answer: 'AI websites integrate intelligent logic—such as real-time user intent parsing, automated personalization, predictive search, and generative assistants—rather than just serving static pages.'
      },
      {
        question: 'Can you integrate AI into an existing website or do we need a rebuild?',
        answer: 'We can integrate modular AI capabilities (e.g. conversational search, customer assistants, recommender systems) into existing platforms, or build modern headless web apps from the ground up.'
      }
    ],
    relatedServices: ['hybrid-mobile-app-development', 'custom-software-solutions', 'saas-development']
  },
  {
    id: 'mobile-apps',
    slug: 'hybrid-mobile-app-development',
    title: 'Hybrid Mobile Apps',
    shortDescription: 'Create cross-platform apps for iOS and Android with intuitive interfaces, reliable performance, and scalable architecture.',
    fullDescription: 'Deliver unified digital mobile experiences across iOS and Android with single-codebase efficiency, near-native performance, and seamless hardware integration.',
    iconName: 'Smartphone',
    accentColor: 'blue',
    challenges: [
      'High cost and duplicated development time maintaining separate native iOS and Android codebases',
      'Inconsistent user experiences between device operating systems',
      'Slow release cycles and difficult synchronization between platforms',
      'Complex offline sync and background processing requirements'
    ],
    solutions: [
      'Single-codebase React Native & Flutter architectures delivering 95%+ code sharing',
      'Native-feel 60fps animations and fluid touch interactions',
      'Secure offline-first data caching and encrypted synchronization',
      'Automated CI/CD pipelines for zero-downtime app store deployments'
    ],
    benefits: [
      '50% faster time-to-market compared to parallel native builds',
      'Uniform feature rollouts across Apple App Store and Google Play',
      'Lower total cost of maintenance, testing, and lifecycle upgrades',
      'Seamless hardware device integration (biometrics, camera, GPS, push notifications)'
    ],
    process: [
      { step: '01', title: 'Device & User Journey Scoping', description: 'Define core mobile workflows, offline scenarios, and platform conventions.' },
      { step: '02', title: 'Native-Grade UI/UX Prototyping', description: 'Design tactile interfaces adhering to Apple Human Interface and Google Material guidelines.' },
      { step: '03', title: 'Cross-Platform Engineering', description: 'Develop robust, type-safe mobile applications with clean state management.' },
      { step: '04', title: 'App Store Submission & QA', description: 'Execute automated device testing, beta distribution, and complete store submission.' }
    ],
    faqs: [
      {
        question: 'Will a hybrid mobile app feel as smooth as a native iOS/Android app?',
        answer: 'Yes. Modern frameworks like React Native and Flutter compile directly to native UI components and GPU-accelerated graphics, providing 60-120fps smoothness identical to native apps.'
      },
      {
        question: 'Do you manage Apple App Store and Google Play submission?',
        answer: 'Yes, we manage the complete submission process, including developer accounts setup, signing certificates, privacy manifests, and store review compliance.'
      }
    ],
    relatedServices: ['ai-website-development', 'custom-software-solutions', 'erp-solutions']
  },
  {
    id: 'custom-software',
    slug: 'custom-software-solutions',
    title: 'Custom Software Solutions',
    shortDescription: 'Tailored software to meet your business needs, simplify workflows, connect systems, and support unique processes.',
    fullDescription: 'Custom engineering built around your proprietary business workflows, eliminating operational bottlenecks and integrating disconnected enterprise systems.',
    iconName: 'Cpu',
    accentColor: 'orange',
    challenges: [
      'Off-the-shelf software failing to match specific operational workflows',
      'Siloed departments relying on manual spreadsheets and redundant data entry',
      'Legacy software platforms that are brittle, unmaintainable, or security risks',
      'Inability to integrate with partner APIs and modern cloud systems'
    ],
    solutions: [
      'Bespoke software architecture engineered precisely to your business rules',
      'Modern microservices and RESTful / GraphQL API orchestration',
      'Legacy system modernization and non-destructive database migrations',
      'Role-based access control (RBAC), multi-tenant security, and complete audit logging'
    ],
    benefits: [
      'Elimination of recurring costly third-party licensing fees',
      '100% intellectual property ownership tailored to your competitive advantage',
      'Drastic reduction in manual errors and operational overhead',
      'Guaranteed scalability with zero dependency on vendor roadmaps'
    ],
    process: [
      { step: '01', title: 'Deep Workflow Analysis', description: 'Map exact operational pain points, data schemas, and integration points.' },
      { step: '02', title: 'Software System Architecture', description: 'Draft robust database ERDs, API contracts, and security architectures.' },
      { step: '03', title: 'Agile Sprint Implementation', description: 'Iterative two-week sprints with test-driven development and demo checkpoints.' },
      { step: '04', title: 'Enterprise Rollout & Training', description: 'Phased deployment, team onboarding, data migration, and 24/7 hypercare support.' }
    ],
    faqs: [
      {
        question: 'Who owns the source code and intellectual property?',
        answer: 'You own 100% of the custom source code, documentation, schemas, and IP upon project completion and milestone sign-off.'
      },
      {
        question: 'Can your software connect with our existing ERP, CRM, or billing tools?',
        answer: 'Yes. We specialize in building secure API connectors and webhooks to synchronize data with Salesforce, SAP, HubSpot, QuickBooks, Stripe, and internal databases.'
      }
    ],
    relatedServices: ['erp-solutions', 'saas-development', 'hybrid-mobile-app-development']
  },
  {
    id: 'erp-solutions',
    slug: 'erp-solutions',
    title: 'ERP & SaaS Solutions',
    shortDescription: 'Streamline operations with scalable ERP, workflows, resource planning, and multi-tenant cloud solutions.',
    fullDescription: 'End-to-end enterprise resource planning and management software to centralize inventory, supply chain, human capital, procurement, and financial reporting.',
    iconName: 'Layers',
    accentColor: 'green',
    challenges: [
      'Fragmented operational visibility across multiple departments and branches',
      'Manual inventory reconciliation and inventory stock-outs',
      'Inconsistent financial reporting and delayed month-end closing',
      'Complex compliance and regulatory audit preparation'
    ],
    solutions: [
      'Unified enterprise dashboards providing real-time financial and operational intelligence',
      'Automated inventory, order fulfillment, and procurement pipelines',
      'Multi-entity and multi-currency accounting with automated ledger reconciliation',
      'Granular audit trails and role-based permissions adhering to enterprise standards'
    ],
    benefits: [
      'Single source of truth for executive decision-making',
      '30-40% reduction in operational cycle times and administrative overhead',
      'Automated inventory forecasting preventing revenue-draining stockouts',
      'Seamless compliance and audit-ready data storage'
    ],
    process: [
      { step: '01', title: 'Process Mapping & Requirements', description: 'Analyze departmental workflows, data flows, and approval hierarchies.' },
      { step: '02', title: 'Data Modeling & Integration Spec', description: 'Design enterprise data schemas, migration plans, and ledger rules.' },
      { step: '03', title: 'Module Build & Pilot Testing', description: 'Configure core modules (HR, Inventory, Billing) with departmental stakeholders.' },
      { step: '04', title: 'Full Cutover & Continuous Support', description: 'Execute zero-downtime cutover, administrator training, and SLA support.' }
    ],
    faqs: [
      {
        question: 'Do you offer custom ERP development or configure existing systems?',
        answer: 'We provide both custom modular ERP systems built specifically for mid-market and enterprise requirements, as well as customization and integration for leading ERP platforms.'
      },
      {
        question: 'How do you handle legacy data migration into a new ERP?',
        answer: 'We follow a rigorous ETL (Extract, Transform, Load) procedure with automated validation scripts, staging environment reconciliations, and rollback protections.'
      }
    ],
    relatedServices: ['custom-software-solutions', 'saas-development', 'ai-website-development']
  },
  {
    id: 'saas-development',
    slug: 'saas-development',
    title: 'SaaS Product Development',
    shortDescription: 'Scalable software-as-a-service products built around your product vision, multi-tenancy, and subscription models.',
    fullDescription: 'From initial MVP validation to hyperscale enterprise SaaS platforms with multi-tenant architecture, usage-based billing, self-service onboarding, and high availability.',
    iconName: 'Cloud',
    accentColor: 'coral',
    challenges: [
      'High technical debt from hurried MVP builds slowing down feature velocity',
      'Complex multi-tenant data isolation and security requirements',
      'Difficulty implementing flexible subscription, tiering, and billing logic',
      'High churn caused by confusing onboarding flows and sluggish response times'
    ],
    solutions: [
      'Clean multi-tenant cloud architecture with row-level or schema-level isolation',
      'Integrated Stripe / LemonSqueezy subscription engines with usage metering',
      'Self-serve friction-free onboarding with interactive product tours',
      'Scalable Kubernetes and serverless backends engineered for 99.99% uptime'
    ],
    benefits: [
      'Accelerated time-to-revenue with battle-tested SaaS foundation components',
      'Effortless scaling from 10 to 100,000+ active tenants without re-architecture',
      'Institutional-grade security compliance ready for enterprise procurement',
      'Actionable product analytics to track MRR, ARR, churn, and feature adoption'
    ],
    process: [
      { step: '01', title: 'Product Strategy & Monetization', description: 'Define core personas, value metrics, tiering, and API-first philosophy.' },
      { step: '02', title: 'Architecture & Multi-Tenant Scaffolding', description: 'Establish tenant isolation, authentication (SSO/SAML), and billing structures.' },
      { step: '03', title: 'Feature Development & SDK Integration', description: 'Rapid development of user-facing dashboard, admin portals, and external APIs.' },
      { step: '04', title: 'Launch, Growth & Observability', description: 'Implement APM monitoring, customer feedback loops, and automated scaling.' }
    ],
    faqs: [
      {
        question: 'How do you ensure tenant data isolation in multi-tenant SaaS?',
        answer: 'We implement strict tenant isolation using cryptographic tenant IDs, row-level security (RLS) in modern databases, or dedicated tenant schemas depending on your compliance requirements.'
      },
      {
        question: 'Can you help us build an MVP within 6 to 10 weeks?',
        answer: 'Yes. We utilize modular component libraries and pre-tested auth and billing scaffolding to launch robust, production-ready MVPs rapidly without sacrificing code quality.'
      }
    ],
    relatedServices: ['custom-software-solutions', 'ai-website-development', 'erp-solutions']
  },
  {
    id: 'digital-marketing',
    slug: 'digital-marketing',
    title: 'Digital Marketing & Branding',
    shortDescription: 'Grow your brand with data-driven marketing strategies, technical SEO, content, and high-ROI acquisition campaigns.',
    fullDescription: 'Data-driven customer acquisition, conversion rate optimization (CRO), brand identity systems, and technical search engine dominance that drives pipeline revenue.',
    iconName: 'BarChart3',
    accentColor: 'orange',
    challenges: [
      'Wasted ad spend on low-intent clicks without clear attribution or pipeline impact',
      'Low organic visibility due to technical SEO deficiencies and weak content strategy',
      'Inconsistent brand voice and identity across modern digital channels',
      'High customer acquisition costs (CAC) eroding unit economics'
    ],
    solutions: [
      'Full-funnel performance marketing across Google Ads, LinkedIn, and Meta Ads',
      'Technical SEO architecture, programmatic content, and authoritative backlinks',
      'Conversion rate optimization with continuous A/B testing and funnel analytics',
      'Distinctive corporate brand identity systems, guidelines, and visual assets'
    ],
    benefits: [
      'Predictable flow of qualified inbound leads and demo requests',
      'Long-term organic search dominance for high-intent commercial keywords',
      'Lower CAC through continuous landing page optimization and targeting refinements',
      'Unified, prestigious brand reputation that builds buyer trust immediately'
    ],
    process: [
      { step: '01', title: 'Audit & Market Intelligence', description: 'Evaluate competitive landscape, keyword whitespace, and conversion funnels.' },
      { step: '02', title: 'Go-To-Market & Creative Strategy', description: 'Develop targeted messaging, value propositions, and high-converting creative assets.' },
      { step: '03', title: 'Multi-Channel Execution', description: 'Launch coordinated SEO, search campaigns, social amplification, and retargeting.' },
      { step: '04', title: 'Analytics & Revenue Attribution', description: 'Weekly performance sprints with clear attribution tracking down to closed pipeline.' }
    ],
    faqs: [
      {
        question: 'How do you measure digital marketing success?',
        answer: 'We measure true business metrics: Customer Acquisition Cost (CAC), Return on Ad Spend (ROAS), Qualified Pipeline Generated, and Organic Revenue Growth.'
      },
      {
        question: 'Do you provide technical SEO alongside website development?',
        answer: 'Yes. Every website and web app we engineer is built with semantic HTML, JSON-LD schema markup, optimized Core Web Vitals, and fast server-rendered meta tags.'
      }
    ],
    relatedServices: ['ai-website-development', 'saas-development', 'hybrid-mobile-app-development']
  }
];

export const mobileAppSubServices: SubServiceItem[] = [
  {
    title: 'Custom Mobile App Development',
    description: 'Tailored mobile applications built from scratch to address proprietary enterprise workflows and customer experiences.',
    iconName: 'Smartphone'
  },
  {
    title: 'Cross-Platform Development',
    description: 'Unified React Native and Flutter applications delivering identical 60fps performance on both iOS and Android.',
    iconName: 'Layers'
  },
  {
    title: 'Native App Development',
    description: 'High-performance Swift and Kotlin development for compute-heavy apps requiring direct hardware-level optimization.',
    iconName: 'Code'
  },
  {
    title: 'E-Commerce App Development',
    description: 'Fast, secure mobile storefronts featuring one-touch checkout, live inventory sync, and intelligent product feeds.',
    iconName: 'ShoppingCart'
  },
  {
    title: 'API Integration',
    description: 'Seamless integration with third-party payment gateways, CRM systems, cloud storage, and legacy backends.',
    iconName: 'Network'
  },
  {
    title: 'App Maintenance & Upgrades',
    description: 'Proactive OS version compatibility updates, performance profiling, security patches, and 24/7 uptime monitoring.',
    iconName: 'RefreshCw'
  }
];
